import { useState, useCallback, useEffect } from 'react';
import { Helmet } from "react-helmet";
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Asset Imports
import presentation from '../assets/academy/creative-quarters-black.png';
import img1 from '../assets/academy/guides/1.jpg';
import img2 from '../assets/academy/guides/2.jpg';
import img3 from '../assets/academy/guides/3.jpg';
import img4 from '../assets/academy/guides/4.jpg';
import img5 from '../assets/academy/guides/5.jpg';
import img6 from '../assets/academy/guides/6.jpg';
import img7 from '../assets/academy/guides/7.jpg';
import img8 from '../assets/academy/guides/8.jpg';
import img9 from '../assets/academy/guides/9.jpg';
import img10 from '../assets/academy/guides/10.jpg';

const pillars = [
  {
    number: "01",
    title: "Photography Mentorship & Masterclasses",
    body: "Direct, intensive training with Temitope Jalekun and guest industry leaders. We cover advanced lighting, editorial composition, and the business frameworks needed to scale your creative agency.",
  },
  {
    number: "02",
    title: "The TCQ Podcast",
    body: "Weekly insights into the realities of the creative industry. We discuss everything from navigating high-end commercial contracts to the psychology of directing subjects on set. Available on all major streaming platforms.",
  },
  {
    number: "03",
    title: "Community & Resource Access",
    body: "Join a curated network of driven photographers and cinematographers. Gain access to exclusive creative tools, studio resources, and peer-to-peer portfolio reviews.",
  },
];

const AcademyMore = () => {
  const [currentIndex, setCurrentIndex] = useState(null);
  const [form, setForm] = useState({ fullName: '', email: '', focus: '', portfolio: '' });
  const [formStatus, setFormStatus] = useState(null);

  const images = [img1, img2, img5, img6, img7, img3, img8, img4, img9, img10];

  const openLightbox = (index) => setCurrentIndex(index);
  const closeLightbox = useCallback(() => setCurrentIndex(null), []);

  const showNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const showPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'ArrowLeft') showPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, closeLightbox, showNext, showPrev]);

  const handleFormChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/academy/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setFormStatus('success');
        setForm({ fullName: '', email: '', focus: '', portfolio: '' });
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <>
      <Helmet>
        <title>The Creative Quarters Africa | Photography Academy & Community</title>
        <meta
          name="description"
          content="Empowering the next generation of visual artists through education, mentorship, and access to premium resources. Founded and led by Creative Director Temitope Jalekun."
        />
      </Helmet>

      <Navbar />

      {/* ── LIGHTBOX ─────────────────────────────────────────────────────────── */}
      {currentIndex !== null && (
        <div
          className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-8 right-8 text-white text-5xl font-light hover:text-gray-400 z-[1001]"
            aria-label="Close Lightbox"
          >
            &times;
          </button>
          <button
            onClick={showPrev}
            className="absolute left-4 md:left-8 text-white text-6xl hover:bg-white/10 p-4 rounded-full transition-all z-[1001]"
            aria-label="Previous Image"
          >
            &#10094;
          </button>
          <div className="relative max-w-5xl max-h-[80vh] flex flex-col items-center">
            <img
              src={images[currentIndex]}
              alt={`Gallery view ${currentIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white mt-4 tracking-widest font-light">
              {currentIndex + 1} / {images.length}
            </p>
          </div>
          <button
            onClick={showNext}
            className="absolute right-4 md:right-8 text-white text-6xl hover:bg-white/10 p-4 rounded-full transition-all z-[1001]"
            aria-label="Next Image"
          >
            &#10095;
          </button>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative h-screen min-h-[600px] overflow-hidden bg-black flex items-end">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url(${presentation})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20 z-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30 z-10" />

        <div className="relative z-20 w-full xl:px-44 lg:px-24 px-6 pb-16 xl:pb-24">
          <p
            className="text-white/50 uppercase tracking-[.4em] text-xs mb-5"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Photography Academy &amp; Community
          </p>
          <h1 className="text-white uppercase xl:text-6xl lg:text-5xl md:text-4xl text-3xl leading-[1.05] tracking-[.08em] mb-4 max-w-4xl">
            The Creative Quarters Africa
          </h1>
          <p
            className="text-white/50 uppercase tracking-[.2em] text-xs mb-6"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Photography Academy &amp; Community
          </p>
          <p
            className="text-white/70 text-sm md:text-base max-w-xl leading-relaxed mb-10"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Empowering the next generation of visual artists through education, mentorship,
            and access to premium resources. Founded and led by Creative Director Temitope Jalekun.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#join"
              className="bg-white text-black px-8 py-3 text-xs tracking-[.25em] uppercase hover:bg-white/90 transition-all"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Join the Community
            </a>
            <a
              href="https://www.youtube.com/@ThecreativeQuarters24"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/60 text-white px-8 py-3 text-xs tracking-[.25em] uppercase hover:border-white hover:bg-white/10 transition-all"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Listen to the Podcast
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 right-8 xl:right-44 z-20 flex flex-col items-center gap-2 opacity-40">
          <span
            className="text-white text-[10px] tracking-[.3em] uppercase rotate-90 origin-center"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Scroll
          </span>
          <div className="w-px h-10 bg-white/50" />
        </div>
      </section>

      {/* ── SECTION 2: THE TCQ MISSION ───────────────────────────────────────── */}
      <section className="bg-black border-t border-white/10 xl:px-44 lg:px-24 px-6 py-24 xl:py-32">
        <p
          className="text-white/40 uppercase tracking-[.4em] text-xs mb-6"
          style={{ fontFamily: 'Muli, sans-serif' }}
        >
          Our Mission
        </p>
        <h2 className="text-white uppercase xl:text-5xl lg:text-4xl text-3xl tracking-[.08em] leading-tight mb-10 max-w-3xl">
          Elevating the Standard of Creative Photography
        </h2>
        <div className="grid xl:grid-cols-2 gap-12 items-start">
          <p
            className="text-white/60 text-sm md:text-base leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            The photography industry thrives on shared knowledge. The Creative Quarters Africa (TCQ)
            was built to bridge the gap between emerging talent and sustainable, high-level industry
            success. We move beyond basic camera tutorials, focusing heavily on the intersection of
            art, business acumen, and authentic storytelling.
          </p>
          <p
            className="text-white/60 text-sm md:text-base leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Through structured mentorship and a collaborative community, we equip African and global
            creatives with the tools required to build profitable, world-class brands.
          </p>
        </div>
      </section>

      {/* ── SECTION 3: EDUCATION & RESOURCES ────────────────────────────────── */}
      <section className="bg-black border-t border-white/10 xl:px-44 lg:px-24 px-6 py-24 xl:py-32">
        <p
          className="text-white/40 uppercase tracking-[.4em] text-xs mb-6"
          style={{ fontFamily: 'Muli, sans-serif' }}
        >
          Education &amp; Resources
        </p>
        <h2 className="text-white uppercase xl:text-4xl lg:text-3xl text-2xl tracking-[.08em] leading-tight mb-16 max-w-2xl">
          Three Pillars of the Academy
        </h2>

        <div className="grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-px bg-white/10">
          {pillars.map((pillar) => (
            <div key={pillar.number} className="bg-black p-10 flex flex-col gap-6">
              <span
                className="text-white/20 text-5xl font-light leading-none"
              >
                {pillar.number}
              </span>
              <h3
                className="text-white uppercase tracking-[.08em] text-sm md:text-base leading-snug"
              >
                {pillar.title}
              </h3>
              <p
                className="text-white/50 text-sm leading-relaxed"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                {pillar.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECTION 4: SEO BLOCK ─────────────────────────────────────────────── */}
      <section className="bg-white xl:px-44 lg:px-24 px-6 py-24 xl:py-32">
        <p
          className="text-black/40 uppercase tracking-[.4em] text-xs mb-6"
          style={{ fontFamily: 'Muli, sans-serif' }}
        >
          About the Programme
        </p>
        <h2 className="text-black uppercase xl:text-4xl lg:text-3xl text-2xl tracking-[.08em] leading-tight mb-8 max-w-2xl">
          Professional Photography Training and Mentorship
        </h2>
        <div className="grid xl:grid-cols-2 gap-12">
          <p
            className="text-black/60 text-sm md:text-base leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Developing a successful career requires more than raw talent; it requires guidance and
            structural knowledge. The Creative Quarters Africa provides comprehensive photography
            training and business mentorship designed specifically for ambitious visual artists.
          </p>
          <p
            className="text-black/60 text-sm md:text-base leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Whether you are transitioning into commercial brand photography or scaling a luxury
            wedding business, our academy offers the strategic insights and community support
            necessary to refine your craft and command premium rates in a competitive market.
          </p>
        </div>
      </section>

      {/* ── SECTION 5: JOIN THE MOVEMENT ─────────────────────────────────────── */}
      <section id="join" className="bg-black border-t border-white/10 xl:px-44 lg:px-24 px-6 py-24 xl:py-32">
        <div className="grid xl:grid-cols-2 gap-16 items-start">
          <div className="flex flex-col">
            <p
              className="text-white/40 uppercase tracking-[.4em] text-xs mb-6"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Join the Movement
            </p>
            <h2 className="text-white uppercase xl:text-5xl lg:text-4xl text-3xl tracking-[.08em] leading-tight mb-8">
              Step Into the Creative Quarters
            </h2>
            <p
              className="text-white/60 text-sm leading-relaxed mb-12"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Ready to refine your visual language and scale your business? Apply for our upcoming
              mentorship cohorts or tune into the latest episode of the TCQ Podcast.
            </p>

            <div className="border-t border-white/10 pt-8">
              <p
                className="text-white/30 uppercase tracking-[.3em] text-[10px] mb-6"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Find us on
              </p>
              <div className="flex items-center gap-8">
                <a
                  href="https://chat.whatsapp.com/FkOjJDOtoLWDvOZt1Pjf5t"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:opacity-70 transition-opacity"
                >
                  <div className="p-2.5 border border-white/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  </div>
                  <span className="text-white/50 text-xs uppercase tracking-[.2em]" style={{ fontFamily: 'Muli, sans-serif' }}>WhatsApp</span>
                </a>
                <a
                  href="https://www.instagram.com/thecreativequartersafrica"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:opacity-70 transition-opacity"
                >
                  <div className="p-2.5 border border-white/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                  </div>
                  <span className="text-white/50 text-xs uppercase tracking-[.2em]" style={{ fontFamily: 'Muli, sans-serif' }}>Instagram</span>
                </a>
                <a
                  href="https://www.youtube.com/@ThecreativeQuarters24"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:opacity-70 transition-opacity"
                >
                  <div className="p-2.5 border border-white/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.42a2.78 2.78 0 0 0-1.94 2C1 8.14 1 12 1 12s0 3.86.42 5.58a2.78 2.78 0 0 0 1.94 2c1.71.42 8.6.42 8.6.42s6.88 0 8.6-.42a2.78 2.78 0 0 0 1.94-2C23 15.86 23 12 23 12s0-3.86-.42-5.58z"></path><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"></polygon></svg>
                  </div>
                  <span className="text-white/50 text-xs uppercase tracking-[.2em]" style={{ fontFamily: 'Muli, sans-serif' }}>YouTube</span>
                </a>
              </div>
            </div>
          </div>

          {/* Application Form */}
          <form onSubmit={handleFormSubmit} className="space-y-5">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="fullName"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Full Name
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={form.fullName}
                onChange={handleFormChange}
                required
                className="border border-white/20 bg-transparent text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleFormChange}
                required
                className="border border-white/20 bg-transparent text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="focus"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Current Focus
              </label>
              <select
                id="focus"
                name="focus"
                value={form.focus}
                onChange={handleFormChange}
                required
                className="border border-white/20 bg-black text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              >
                <option value="">Select…</option>
                <option value="Wedding">Wedding</option>
                <option value="Commercial">Commercial</option>
                <option value="Portrait">Portrait</option>
                <option value="Just Starting">Just Starting</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="portfolio"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Link to Portfolio / Instagram
              </label>
              <input
                id="portfolio"
                name="portfolio"
                type="url"
                value={form.portfolio}
                onChange={handleFormChange}
                className="border border-white/20 bg-transparent text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              />
            </div>

            <button
              type="submit"
              disabled={formStatus === 'loading'}
              className="mt-2 bg-white text-black px-10 py-3 text-xs tracking-[.25em] uppercase hover:bg-white/90 transition-all disabled:opacity-50"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              {formStatus === 'loading' ? 'Submitting…' : 'Apply for Membership'}
            </button>

            {formStatus === 'success' && (
              <p className="text-sm text-green-400 uppercase tracking-widest" style={{ fontFamily: 'Muli, sans-serif' }}>
                Application received — we'll be in touch shortly.
              </p>
            )}
            {formStatus === 'error' && (
              <p className="text-sm text-red-400 uppercase tracking-widest" style={{ fontFamily: 'Muli, sans-serif' }}>
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        </div>
      </section>

      {/* ── GALLERY ──────────────────────────────────────────────────────────── */}
      <section className="bg-black border-t border-white/10 xl:px-44 lg:px-24 px-6 py-24">
        <p
          className="text-white/40 uppercase tracking-[.4em] text-xs mb-12"
          style={{ fontFamily: 'Muli, sans-serif' }}
        >
          Community Moments
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
          {images.map((img, index) => (
            <div
              key={index}
              className="overflow-hidden group cursor-pointer aspect-square"
              onClick={() => openLightbox(index)}
            >
              <img
                src={img}
                alt={`TCQ Academy moment ${index + 1}`}
                className="w-full h-full object-cover transform transition-all duration-500 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AcademyMore;
